import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plane, Search, FileSpreadsheet, FileText, CalendarDays } from "lucide-react";
import { api, API, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

const TR_DAY = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const fmtDay = (iso) => {
  if (!iso) return "Tarihi girilmemiş";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" })} · ${TR_DAY[d.getDay()]}`;
};

function Row({ r, modes, onSave, canPhone }) {
  const [f, setF] = useState({
    arrival_date: r.arrival_date, return_date: r.return_date,
    travel_mode: r.travel_mode, travel_greeter: r.travel_greeter, travel_note: r.travel_note,
    accommodation: r.accommodation, accommodation_note: r.accommodation_note,
  });
  const [busy, setBusy] = useState(false);
  const key = `${r.arrival_date}|${r.return_date}|${r.travel_mode}|${r.travel_greeter}|${r.travel_note}|${r.accommodation}|${r.accommodation_note}`;
  useEffect(() => {
    setF({
      arrival_date: r.arrival_date, return_date: r.return_date,
      travel_mode: r.travel_mode, travel_greeter: r.travel_greeter, travel_note: r.travel_note,
      accommodation: r.accommodation, accommodation_note: r.accommodation_note,
    });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    setBusy(true);
    try {
      const body = {};
      const clear = [];
      Object.entries(patch).forEach(([k, v]) => {
        if (v === "" || v == null) clear.push(k);
        else body[k] = v;
      });
      if (clear.length) body.clear_fields = clear;
      await api.patch(`/voters/${r.id}`, body);
      await onSave();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); save({ [k]: v }); };

  return (
    <tr className="border-t border-border align-top" data-testid={`travel-row-${r.id}`}>
      <td className="px-2 py-2">
        <span className="block font-medium truncate max-w-[220px]">{r.company_name}</span>
        <span className="text-[11px] text-muted-foreground">
          #{r.committee_no} · {r.branch}{r.city ? ` · ${r.city}` : ""}
          {r.prediction ? ` · ${r.prediction}` : ""}
        </span>
      </td>
      <td className="px-2 py-2">
        <span className="block text-xs truncate max-w-[150px]">{r.voting_person || "—"}</span>
        {canPhone && r.phone && <span className="text-[11px] text-muted-foreground">{r.phone}</span>}
      </td>
      <td className="px-1 py-2">
        <Input type="date" className="h-8 w-[140px] text-xs" value={f.arrival_date} disabled={busy}
          onChange={(e) => set("arrival_date", e.target.value)}
          data-testid={`travel-arrival-${r.id}`} />
      </td>
      <td className="px-1 py-2">
        <Input type="date" className="h-8 w-[140px] text-xs" value={f.return_date} disabled={busy}
          onChange={(e) => set("return_date", e.target.value)}
          data-testid={`travel-return-${r.id}`} />
      </td>
      <td className="px-1 py-2">
        <select value={f.travel_mode} disabled={busy}
          onChange={(e) => set("travel_mode", e.target.value)}
          data-testid={`travel-mode-${r.id}`}
          className="h-8 w-[108px] rounded-md border border-border bg-background px-1.5 text-xs">
          <option value="">—</option>
          {Object.entries(modes).map(([k, lb]) => <option key={k} value={k}>{lb}</option>)}
        </select>
      </td>
      <td className="px-1 py-2">
        <Input className="h-8 w-[140px] text-xs" defaultValue={f.travel_greeter} disabled={busy}
          placeholder="Karşılayacak kişi"
          onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.travel_greeter) save({ travel_greeter: v }); }}
          data-testid={`travel-greeter-${r.id}`} />
      </td>
      <td className="px-1 py-2">
        <div className="flex items-center gap-2">
          <Checkbox checked={!!f.accommodation} disabled={busy}
            onCheckedChange={(ck) => { setF((s) => ({ ...s, accommodation: !!ck })); save({ accommodation: !!ck }); }}
            data-testid={`travel-accommodation-${r.id}`} />
          {f.accommodation && (
            <Input className="h-8 w-[130px] text-xs" defaultValue={f.accommodation_note} disabled={busy}
              placeholder="Otel / oda"
              onBlur={(e) => { const v = e.target.value.trim(); if (v !== (r.accommodation_note || "")) save({ accommodation_note: v }); }}
              data-testid={`travel-accommodation-note-${r.id}`} />
          )}
        </div>
      </td>
      <td className="px-1 py-2">
        <Input className="h-8 w-[190px] text-xs" defaultValue={f.travel_note} disabled={busy}
          placeholder="Uçuş/saat, karşılama"
          onBlur={(e) => { const v = e.target.value.trim(); if (v !== r.travel_note) save({ travel_note: v }); }}
          data-testid={`travel-note-${r.id}`} />
      </td>
      <td className="px-2 py-2 whitespace-nowrap text-[11px] text-muted-foreground">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (r.proxy_document ? "belgeli" : "")}
      </td>
    </tr>
  );
}

export const TravelPlan = ({ committeeNo = null, lockCommittee = false, title = "Seyahat Planı" }) => {
  const [cno, setCno] = useState(committeeNo);
  const [branch, setBranch] = useState("");
  const [filt, setFilt] = useState("planned");
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);

  useEffect(() => { if (lockCommittee) setCno(committeeNo); }, [committeeNo, lockCommittee]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/travel-plan", {
        params: {
          committee_no: cno || undefined, branch: branch || undefined,
          filter: filt, q: q.trim() || undefined,
        },
      });
      setD(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  }, [cno, branch, filt, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportUrl = (format) => {
    const p = new URLSearchParams({ format, filter: filt });
    if (cno) p.set("committee_no", cno);
    if (branch) p.set("branch", branch);
    if (q.trim()) p.set("q", q.trim());
    return `${API}/travel-plan/export?${p.toString()}`;
  };

  const download = async (format) => {
    try {
      const { data } = await api.get(exportUrl(format).replace(API, ""), { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `seyahat_plani.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, e));
    }
  };

  if (!d) {
    return <div className="flex items-center gap-2 p-6 text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor...
    </div>;
  }

  const groups = [];
  const seen = new Map();
  d.rows.forEach((r) => {
    const k = r.arrival_date || "";
    if (!seen.has(k)) { seen.set(k, { date: k, rows: [] }); groups.push(seen.get(k)); }
    seen.get(k).rows.push(r);
  });

  return (
    <div className="space-y-4" data-testid="travel-plan">
      <Card className="p-4 border border-border">
        <div className="flex flex-wrap items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#0F2C59]/10 text-[#0F2C59]">
            <Plane className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-base md:text-lg font-bold">{title}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Şehir dışından gelecek yetkililerin geliş/dönüş günü, ulaşım şekli ve karşılama
              sorumlusu. Değişiklikler anında kaydedilir.
            </p>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="travel-export-xlsx">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
            <Button size="sm" variant="outline" onClick={() => download("pdf")} data-testid="travel-export-pdf">
              <FileText className="h-4 w-4 mr-1.5" /> PDF
            </Button>
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {!lockCommittee && (
            <select value={cno || ""} onChange={(e) => setCno(e.target.value ? Number(e.target.value) : null)}
              data-testid="travel-committee"
              className="h-9 rounded-md border border-border bg-background px-2 text-sm">
              <option value="">Tüm komiteler</option>
              {d.committees.map((c) => (
                <option key={c.committee_no} value={c.committee_no}>
                  K{c.committee_no} — {c.name} ({c.planned}/{c.total})
                </option>
              ))}
            </select>
          )}
          <select value={branch} onChange={(e) => setBranch(e.target.value)} data-testid="travel-branch"
            className="h-9 rounded-md border border-border bg-background px-2 text-sm">
            <option value="">Tüm şubeler</option>
            <option value="Merkez">Merkez</option>
            {d.branches.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select value={filt} onChange={(e) => setFilt(e.target.value)} data-testid="travel-filter"
            className="h-9 rounded-md border border-border bg-background px-2 text-sm">
            <option value="planned">Planı girilenler</option>
            <option value="missing">Planı girilmeyenler</option>
            <option value="all">Tümü</option>
          </select>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma / sicil / karşılayan"
              className="h-9 pl-8" data-testid="travel-search" />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2">
          <Badge variant="outline" data-testid="travel-stat-total">{d.total} firma</Badge>
          <Badge className="bg-emerald-600 text-white" data-testid="travel-stat-planned">
            {d.planned} planlı
          </Badge>
          {d.accommodation > 0 && (
            <Badge className="bg-sky-600 text-white" data-testid="travel-stat-accommodation">
              {d.accommodation} konaklamalı
            </Badge>
          )}
          {d.no_greeter > 0 && (
            <Badge className="bg-amber-500 text-white" data-testid="travel-stat-no-greeter">
              {d.no_greeter} karşılama sorumlusu atanmamış
            </Badge>
          )}
          {d.days.slice(0, 6).map((day) => (
            <Badge key={day.date} variant="outline" className="gap-1">
              <CalendarDays className="h-3 w-3" /> {day.date} · {day.arrivals} geliş / {day.returns} dönüş
            </Badge>
          ))}
        </div>
      </Card>

      {groups.length === 0 && (
        <Card className="p-6 border border-dashed border-border text-sm text-muted-foreground"
          data-testid="travel-empty">
          Bu filtrede kayıt yok. “Planı girilmeyenler” seçeneğiyle plan girilecek firmaları listeleyin.
        </Card>
      )}

      {groups.map((g) => (
        <Card key={g.date || "none"} className="p-0 border border-border overflow-hidden"
          data-testid={`travel-group-${g.date || "none"}`}>
          <div className="flex flex-wrap items-center gap-2 bg-muted/70 px-4 py-2.5">
            <CalendarDays className="h-4 w-4 text-[#0F2C59]" />
            <h3 className="font-display font-semibold text-sm">{fmtDay(g.date)}</h3>
            <span className="text-xs text-muted-foreground">{g.rows.length} kayıt</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[1180px]">
              <thead className="bg-muted/40 text-xs">
                <tr className="text-left">
                  {["Firma", "Gelecek kişi", "Geliş", "Dönüş", "Ulaşım", "Karşılama sorumlusu", "Konaklama", "Not", ""].map((h) => (
                    <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r) => (
                  <Row key={r.id} r={r} modes={d.travel_modes} onSave={load}
                    canPhone={d.can_see_phone} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default TravelPlan;
